// ============================================================================
// Checklist Books — 3 books per workspace, keyed by ChecklistType.
// ----------------------------------------------------------------------------
// Replaces the singleton `ChecklistItem` model (config + occurrences map in
// one doc). The new physical model is four kinds of docs:
//
//   1. Definition   — checklists/{checklistId}
//                     One doc per checklist. All static config: title, form,
//                     completion criteria, proof rules, dependencies, assignee,
//                     stock, delay policy, access, checklistType, and a
//                     `parentContainerId` ref to a Goal/Task/Subtask (or the
//                     workspace-default root). The canvas is scoped by book
//                     first, then filtered by the active breadcrumb's parent /
//                     descendant scope inside that book.
//
//   2. Book         — checklist_books/{workspaceId}_{type}
//                     Exactly three per workspace: `_INDIVIDUAL`, `_DAILY`,
//                     `_WEEKLY`. Holds book-level metadata and hosts the
//                     `pages/` and `layout_versions/` subcollections.
//
//   3. Page         — checklist_books/{bookId}/pages/{pageKey}
//                     Per-occurrence state for the whole canvas.
//                       • INDIVIDUAL → single page, pageKey = 'single'
//                       • DAILY      → pageKey = ISO date  (YYYY-MM-DD)
//                       • WEEKLY     → pageKey = ISO week  (YYYY-Www)
//                     Pre-created lookahead = today + N based on
//                     WorkspaceRecurringSettings.lookaheadDays (DAILY / WEEKLY
//                     only; INDIVIDUAL is always exactly 1 page).
//                     Holds `layoutVersionId` + inline `completions` map for
//                     single-read canvas rendering.
//
//   4. LayoutVer    — checklist_books/{bookId}/layout_versions/{versionId}
//                     Immutable snapshot of the canvas (positions, wires,
//                     hidden nodes). A page edit mints a new version and
//                     forward-propagates: repoints pages sharing the edited
//                     page's current versionId, stops at the first divergent
//                     page. New page creation inherits from the nearest prior
//                     page's versionId (falling back to the book's
//                     activeLayoutVersionId on the very first page).
//
// Read pattern for a book at page D:
//     book       = getDoc('checklist_books/{ws}_{type}')                   (1 read)
//     page       = getDoc('checklist_books/{ws}_{type}/pages/{D}')          (1 read)
//     layout     = getDoc('checklist_books/{ws}_{type}/layout_versions/{page.layoutVersionId}')  (1 read)
//     definitions= getDocs('checklists').where(workspaceId==ws, checklistType==type)  (N reads once, cache)
//     ═══════════════════════════════════════════════════════════════════════
//     Total steady-state: ~3 doc reads to render the canvas at a new date.
//
// Proof submissions and comments remain top-level collections (they carry
// files / threads); they now FK on `(checklistId, pageId)` rather than the
// old `checklistItemId`.
//
// See MEMORY: gamification-monetization-net, contribution-heatmap — those
// fanouts key on `(checklistId, pageKey)` now.
// ============================================================================

import type {
  BoardStatus,
  CanvasElementType,
  ChecklistNodeKind,
  ChecklistNodeLink,
  ChecklistScheduleMode,
  ChecklistItemBindings,
  ChecklistItemSideEffects,
  ChecklistWorkflowStatus,
  TimelineBoardKey,
  ChecklistItemEduKind,
} from './schema'

// ---------------------------------------------------------------------------
// Shared kinds
// ---------------------------------------------------------------------------

/** The three books a workspace has. */
export type ChecklistType = 'INDIVIDUAL' | 'DAILY' | 'WEEKLY'

/** Types whose books have a growing set of pages (i.e. every type EXCEPT
 *  INDIVIDUAL, which is fixed at exactly one page). */
export type RecurringChecklistType = Exclude<ChecklistType, 'INDIVIDUAL'>

/** Page identifier:
 *   INDIVIDUAL → 'single'
 *   DAILY      → ISO date  (YYYY-MM-DD)
 *   WEEKLY     → ISO week  (YYYY-Www) */
export type PageKey = string

/** Random Firestore auto-id for a layout version. */
export type LayoutVersionId = string

/** Random Firestore auto-id for a detail/content version. */
export type DetailVersionId = string

/** Compound id of a book: `{workspaceId}_{type}`. */
export type BookId = string

export const INDIVIDUAL_PAGE_KEY: PageKey = 'single'

export const bookIdFor = (workspaceId: string, type: ChecklistType): BookId =>
  `${workspaceId}_${type}`

// ---------------------------------------------------------------------------
// 1) Definition — checklists/{checklistId}
// ---------------------------------------------------------------------------

/**
 * A single checklist. All fields that describe what the checklist IS live
 * here. No per-page state, no canvas position — those live on the book's
 * page and layout-version docs.
 */
export interface ChecklistDefinition {
  id: string
  workspaceId: string
  workspaceSlug?: string

  /** Which book this checklist belongs to. Set once at create; changing type
   *  soft-archives the old book's node and re-adds the checklist to the new
   *  book. */
  checklistType: ChecklistType

  /** Timeline parent (Goal / Task / Subtask id, or the reserved
   *  workspace-default id). Used both for breadcrumb display and for the
   *  active parent/descendant filter inside a book-scoped canvas. */
  parentContainerId: string
  board?: TimelineBoardKey
  publicId?: string
  routePath?: string
  parentRoutePath?: string
  deletedFromBoard?: TimelineBoardKey | null
  deletedAt?: unknown

  // --- Content ---
  title: string
  description?: string
  completionCriteria?: string
  priority: number
  weightage: number
  isDescriptionPrivate?: boolean

  // --- Archive ---
  isArchived?: boolean
  archivedAt?: string | null
  archivedById?: string | null
  archivedByName?: string | null
  preArchiveChecklistType?: ChecklistType | null

  // --- Scheduling / calendar ---
  scheduleMode?: ChecklistScheduleMode
  scheduleStartDate?: string | null
  scheduleEndDate?: string | null
  searchDateBuckets?: string[]
  hasTimeWindow?: boolean
  scheduleTimeFrom?: string | null
  scheduleTimeTo?: string | null
  startDate?: string | null
  endDate?: string | null
  countdownHours?: number | null

  // --- Assignment ---
  assigneeId?: string | null
  assigneeName?: string | null
  assigneeHistoryIds?: string[]
  /** Provenance for trusted Analysis alert and governance conversion tasks. */
  source?: 'analysis-alert' | 'analysis-row-convert'
  sourceRuleId?: string
  sourceRowAnchor?: string

  // --- Attachments (form/design definitions, cover art) ---
  attachedFormIds?: string[]
  attachedDesignIds?: string[]
  attachedUploadIds?: string[]
  attachedLinkIds?: string[]
  cardBackgroundFileId?: string | null
  cardBackgroundUrl?: string | null
  cardBackgroundFileName?: string | null
  cardBackgroundStoragePath?: string | null

  // --- Dependencies & gates (semantic; canvas wires may or may not mirror) ---
  prerequisites: string[]
  dependencyNodeIds?: string[]
  invalidDependencyNodeIds?: string[]
  blockedByExternal: {
    targetType: CanvasElementType
    targetId: string
  } | null
  blockedByExternalId?: string | null
  interWorkspaceTargetId?: string | null
  dependencyLogicGate?: 'ALL_PREREQUISITES_MET' | 'ANY_PREREQUISITE_MET'
  blockedByNodeIds?: ChecklistNodeLink[]
  isBlockerForNodeIds?: ChecklistNodeLink[]
  nodeKind?: ChecklistNodeKind
  gateInputIds?: string[]
  hasAutoSpawnTrigger?: boolean
  autoSpawnTemplates?: Array<{
    title: string
    weightage: number
    relativeYOffset: number
  }>

  // --- Proof / verification RULES (submissions live per-page) ---
  requiresProofFile?: boolean
  requiresCoSign?: boolean
  coSignerRoleTarget?: 'ADMIN' | 'MANAGER' | string
  proofApproverId?: string | null
  proofApproverName?: string | null
  requiresLocationPin?: boolean
  targetCoordinates?: { lat: number; lng: number } | null

  // --- Timer / delay policy ---
  enforcePaintSpringDelay?: boolean
  delayDurationHours?: number

  // --- Risk & resource matrix ---
  estimatedHours?: number | null
  complexityStars?: number
  urgencyStars?: number
  importanceStars?: number
  currencyCode?: string

  // --- Access ---
  helperVisibilityMask?: Record<string, boolean>
  isHelperVisible?: boolean
  readAccess?: string[]
  writeAccess?: string[]
  scopeRead?: string[]
  scopeWrite?: string[]

  // --- Stock module ---
  requiredStockIds?: string[]
  stockRequirements?: Array<{
    stockItemId: string
    quantity: number
    unitOverride?: string | null
    required?: boolean
  }>
  consumptionMode?: 'reserve' | 'consume'
  bindings?: ChecklistItemBindings
  /** Server-only. Cloud Functions append lifecycle effects; clients cannot write it. */
  sideEffects?: ChecklistItemSideEffects

  // --- Recurring (parent-scoped) ---
  /** Only set when `checklistType` is DAILY or WEEKLY. Mirrors the parent
   *  Task/Goal/Subtask's start and end dates at creation time; refreshed by
   *  the parent-date-change reconciler when the parent moves. Individual
   *  page docs materialize lazily inside this range; navigating outside
   *  reads/writes nothing. */
  recurringWindow?: {
    parentElementId: string
    /** ISO YYYY-MM-DD */
    startDate: string
    /** ISO YYYY-MM-DD (inclusive) */
    endDate: string
    /** Firestore Timestamp of last mirror from parent. */
    syncedAt?: unknown
  }
  /** Period key (YYYY-MM-DD or YYYY-Www) from which this repeating item is marked not needed. */
  notRequiredFrom?: string | null
  repeatPaused?: boolean
  repeatPausedReason?: string | null
  /** Server-computed progress for repeating (DAILY / WEEKLY) checklists */
  periodProgress?: {
    done: number
    due: number
    ratio: number
    computedAt?: unknown
  }

  // --- Education module ---
  eduKind?: ChecklistItemEduKind
  eduHomework?: {
    dueDate: string
    maxScore?: number | null
    setBy: string
    setAt: unknown
  }

  // --- Audit ---
  createdAt?: unknown
  createdById?: string
  createdByName?: string | null
  updatedAt?: unknown
  lastUpdatedBy?: string | null
  lastUpdatedByName?: string | null
  recordVersion: number
}

/** Fields that participate in recurring detail versioning. Title is
 *  intentionally excluded and remains global on the definition doc. */
export type ChecklistDefinitionVersionPatch = Partial<Pick<
  ChecklistDefinition,
  | 'description'
  | 'completionCriteria'
  | 'priority'
  | 'weightage'
  | 'isDescriptionPrivate'
  | 'countdownHours'
  | 'assigneeId'
  | 'assigneeName'
  | 'assigneeHistoryIds'
  | 'attachedFormIds'
  | 'attachedDesignIds'
  | 'attachedUploadIds'
  | 'attachedLinkIds'
  | 'cardBackgroundFileId'
  | 'cardBackgroundUrl'
  | 'cardBackgroundFileName'
  | 'cardBackgroundStoragePath'
  | 'prerequisites'
  | 'dependencyNodeIds'
  | 'invalidDependencyNodeIds'
  | 'blockedByExternal'
  | 'blockedByExternalId'
  | 'interWorkspaceTargetId'
  | 'dependencyLogicGate'
  | 'blockedByNodeIds'
  | 'isBlockerForNodeIds'
  | 'nodeKind'
  | 'gateInputIds'
  | 'hasAutoSpawnTrigger'
  | 'autoSpawnTemplates'
  | 'requiresProofFile'
  | 'requiresCoSign'
  | 'coSignerRoleTarget'
  | 'proofApproverId'
  | 'proofApproverName'
  | 'requiresLocationPin'
  | 'targetCoordinates'
  | 'enforcePaintSpringDelay'
  | 'delayDurationHours'
  | 'estimatedHours'
  | 'complexityStars'
  | 'urgencyStars'
  | 'importanceStars'
  | 'currencyCode'
  | 'helperVisibilityMask'
  | 'isHelperVisible'
  | 'readAccess'
  | 'writeAccess'
  | 'requiredStockIds'
  | 'stockRequirements'
  | 'consumptionMode'
>>

// ---------------------------------------------------------------------------
// 2) Book — checklist_books/{workspaceId}_{type}
// ---------------------------------------------------------------------------

/**
 * One of the three books in a workspace. Holds pointers used when a page has
 * no prior page to inherit from, and lightweight metadata.
 */
export interface ChecklistBook {
  id: BookId
  workspaceId: string
  checklistType: ChecklistType

  /** Used when creating the FIRST page of a fresh book, before any page-based
   *  inheritance chain exists. After that, new pages inherit from the nearest
   *  previous page and this is only used as a last-resort fallback. */
  activeLayoutVersionId?: LayoutVersionId | null

  /** Parallel to layout versions, but for versioned checklist detail content
   *  such as assignee, proof rules, ratings, and long-form text. */
  activeDetailVersionId?: DetailVersionId | null

  /** For DAILY / WEEKLY: how many pages ahead of today to keep materialised.
   *  Falls back to WorkspaceRecurringSettings.lookaheadDays when absent. */
  lookaheadPages?: number

  createdAt?: unknown
  updatedAt?: unknown
  recordVersion: number
}

// ---------------------------------------------------------------------------
// 3) Page — checklist_books/{bookId}/pages/{pageKey}
// ---------------------------------------------------------------------------

/**
 * A single occurrence of a book's canvas. Carries every checklist's
 * completion state inline, plus a pointer to the layout version that renders
 * the canvas for this date.
 */
export interface ChecklistBookPage {
  id: PageKey
  bookId: BookId
  workspaceId: string
  checklistType: ChecklistType

  /** DAILY = ISO date, WEEKLY = ISO week, INDIVIDUAL = 'single'. Same as id. */
  pageKey: PageKey
  /** ISO date this page represents:
   *   DAILY      → pageKey
   *   WEEKLY     → Monday of the ISO week
   *   INDIVIDUAL → book.createdAt date (mostly informational). */
  dateIso: string

  /** Points to checklist_books/{bookId}/layout_versions/{layoutVersionId}. */
  layoutVersionId: LayoutVersionId

  /** Points to checklist_books/{bookId}/detail_versions/{detailVersionId}. */
  detailVersionId?: DetailVersionId | null

  /** Per-page overrides used for "Only this instance" edits. Missing/empty
   *  means the page inherits its detail content entirely from its detail
   *  version pointer. */
  detailPatch?: Record<string, ChecklistDefinitionVersionPatch>

  /** Per-checklist state for every node visible on this page. Missing entries
   *  are treated as "not started". */
  completions: Record<string, ChecklistCompletionState>

  /** Aggregated per-page counters, cheap to keep current on every write:
   *  displayed in the page-picker/breadcrumb without an extra query. */
  totalNodes?: number
  completedNodes?: number

  // --- Audit ---
  createdAt?: unknown
  createdById?: string
  createdByName?: string | null
  updatedAt?: unknown
  lastUpdatedBy?: string | null
  lastUpdatedByName?: string | null
  recordVersion: number
}

/**
 * Per-(checklist, page) state. Everything that varies day-to-day for a single
 * checklist. Proof/comments themselves live in top-level collections keyed by
 * (checklistId, pageId); the fields here are the summary the canvas needs.
 */
export interface ChecklistCompletionState {
  isCompleted: boolean
  boardStatus?: BoardStatus | null
  workflowStatus?: ChecklistWorkflowStatus | null
  previousWorkflowStatus?: ChecklistWorkflowStatus | null

  completedAt?: string | null
  completedById?: string | null
  completedByName?: string | null

  // Readiness (per-day inventory scan-out)
  isReady?: boolean
  resourceReadySku?: string | null
  resourceReadyAt?: unknown

  // Proof submission summary — full submission doc lives in
  // checklist_proof_submissions keyed by (checklistId, pageId).
  proofFileUrl?: string | null
  proofFileName?: string | null
  proofFileStoragePath?: string | null
  proofSubmissionId?: string | null
  proofUploadedById?: string | null
  proofUploadedByName?: string | null
  proofUploadedAt?: string | null
  coSignedByUid?: string | null
  coSignedByName?: string | null
  isPendingReview?: boolean
  proofReviewStatus?: 'PENDING' | 'APPROVED' | 'REJECTED' | null
  proofReviewedAt?: string | null
  proofReviewedById?: string | null
  proofReviewedByName?: string | null
  proofRejectionNote?: string | null
  verifiedCoordinates?: { lat: number; lng: number } | null
  proofVideoUrl?: string | null
  memoStoragePath?: string | null
  memoFileName?: string | null
  memoContentType?: string | null
  memoUploadedById?: string | null
  memoUploadedByName?: string | null
  memoUploadedAt?: string | null
  voiceMemoBlobUrl?: string | null

  // Timer state
  timerStartedAt?: string | null
  timerExpiredAt?: string | null
  isUrgentCrisisMode?: boolean

  // Aggregates
  commentsCount?: number
  actualCost?: number | null

  // Sync bookkeeping
  statusSyncOrigin?: string | null
  statusSyncVersion?: number
}

// ---------------------------------------------------------------------------
// 4) LayoutVersion — checklist_books/{bookId}/layout_versions/{versionId}
// ---------------------------------------------------------------------------

/**
 * Immutable-once-written canvas snapshot for a book. Multiple pages in the
 * same book point to the same version; a layout edit mints a new version and
 * forward-propagates.
 */
export interface ChecklistLayoutVersion {
  id: LayoutVersionId
  bookId: BookId
  workspaceId: string

  /** Every checklist visible on this canvas, keyed by checklist definition id.
   *  Node absence means "not on this canvas" (soft-deleted / archived). */
  nodes: Record<
    string,
    {
      checklistId: string
      position: { x: number; y: number }
      hidden?: boolean
    }
  >

  /** Directional wires between nodes. May mirror the dependency graph or be
   *  purely visual — kind distinguishes the two. */
  wires: Array<{
    from: string
    to: string
    kind?: 'dependency' | 'visual'
  }>

  /** Ancestry pointer for history/audit; nullable on the first version. */
  parentVersionId?: LayoutVersionId | null

  createdAt?: unknown
  createdById?: string
  createdByName?: string | null
  recordVersion: number
}

/** Immutable-once-written detail/content snapshot for a book. Multiple pages
 *  in the same book can point to the same version; a content edit mints a new
 *  version and forward-propagates until a later page already diverged. */
export interface ChecklistDetailVersion {
  id: DetailVersionId
  bookId: BookId
  workspaceId: string
  checklistPatches: Record<string, ChecklistDefinitionVersionPatch>
  parentVersionId?: DetailVersionId | null
  createdAt?: unknown
  createdById?: string
  createdByName?: string | null
  recordVersion: number
}

// ---------------------------------------------------------------------------
// Composite shapes for consumers
// ---------------------------------------------------------------------------

/** Everything the canvas needs for a book at a given page. */
export type ChecklistBookView = {
  book: ChecklistBook
  page: ChecklistBookPage
  layoutVersion: ChecklistLayoutVersion
  /** Definitions of every checklist referenced by the layout. May include
   *  extra definitions the caller is caching. */
  definitions: Record<string, ChecklistDefinition>
}

/** Discriminated result from `resolveInheritedVersion` in layoutVersionService. */
export type LayoutResolutionResult =
  | { kind: 'inherited'; versionId: LayoutVersionId; sourcePageKey: PageKey }
  | { kind: 'active'; versionId: LayoutVersionId }
  | { kind: 'none' }

export type DetailResolutionResult =
  | { kind: 'inherited'; versionId: DetailVersionId; sourcePageKey: PageKey }
  | { kind: 'active'; versionId: DetailVersionId }
  | { kind: 'none' }

// ---------------------------------------------------------------------------
// Firestore path helpers — single source of truth
// ---------------------------------------------------------------------------

export const CHECKLISTS_COL = 'checklists'
export const CHECKLIST_BOOKS_COL = 'checklist_books'
export const PAGES_SUBCOL = 'pages'
export const LAYOUT_VERSIONS_SUBCOL = 'layout_versions'
export const DETAIL_VERSIONS_SUBCOL = 'detail_versions'

export const checklistPath = (checklistId: string) =>
  `${CHECKLISTS_COL}/${checklistId}`

export const bookPath = (bookId: BookId) =>
  `${CHECKLIST_BOOKS_COL}/${bookId}`

export const pagePath = (bookId: BookId, pageKey: PageKey) =>
  `${bookPath(bookId)}/${PAGES_SUBCOL}/${pageKey}`

export const layoutVersionPath = (bookId: BookId, versionId: LayoutVersionId) =>
  `${bookPath(bookId)}/${LAYOUT_VERSIONS_SUBCOL}/${versionId}`

export const detailVersionPath = (bookId: BookId, versionId: DetailVersionId) =>
  `${bookPath(bookId)}/${DETAIL_VERSIONS_SUBCOL}/${versionId}`
